import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

function fmtMoney(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `$${n}`;
}

/**
 * Transparency section — the pledge, the causes, the collapse metrics.
 * `compact` prop hides the top eyebrow/headline (used when the parent
 * page already carries a hero — e.g. the /philanthropy page).
 */
export default function Transparency({ compact = false }) {
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    api.get("/philanthropy/ledger").then((r) => setLedger(r.data)).catch(() => {});
  }, []);

  const pct = ledger?.pledge_pct ?? 10;
  const causes = ledger?.causes || [];

  return (
    <section
      id="ledger"
      data-testid="transparency-section"
      className={`relative w-full ${compact ? "py-8 sm:py-12" : "py-24 sm:py-32"} bg-[#050505] ${compact ? "" : "border-t border-white/[0.05]"} overflow-hidden`}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-[0.06] blur-3xl"
             style={{ background: "radial-gradient(circle,#ff5a00,transparent 60%)" }} />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10">
        {!compact && (
          <>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 0.7 }}
              className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00] text-center"
            >
              The Public Record
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              data-testid="transparency-headline"
              className="font-display uppercase text-center leading-[0.95] text-[#ededed] mt-6"
              style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)", letterSpacing: "-0.005em" }}
            >
              Loud on the collapse.
              <br />
              <span className="italic text-[#ff5a00]">Generous where it matters.</span>
            </motion.h2>
          </>
        )}

        {/* Pledge card — big number */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="pledge-card"
          className={`${compact ? "mt-0" : "mt-16"} border border-[#ff5a00]/30 bg-gradient-to-br from-[#12100c] to-[#0b0b0d] px-6 sm:px-12 py-10 sm:py-14 text-center`}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00]">
            The Pledge
          </p>
          <p
            data-testid="pledge-headline"
            className="font-display uppercase text-[#ededed] leading-[0.9] mt-4"
            style={{ fontSize: "clamp(2.5rem, 8vw, 6rem)" }}
          >
            <span className="text-[#ff5a00]" data-testid="pledge-pct">{pct}%</span> of net revenue.
            <br />
            <span className="italic">Every year. On the record.</span>
          </p>
          <p className="font-body text-base sm:text-lg text-[#a1a1aa] mt-6 max-w-2xl mx-auto leading-relaxed">
            Resilience Multimedia Studios donates {pct}% of annual net revenue to the causes
            below. Percentage adjustable, causes named, receipts follow.
          </p>
        </motion.div>

        {/* Causes */}
        <div className="grid sm:grid-cols-2 gap-6 mt-16">
          {causes.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.05 }}
              data-testid={`cause-${i}`}
              className="group relative border border-[#27272a] bg-[#0b0b0d]/60 backdrop-blur px-6 py-6 hover:border-[#ff5a00]/50 transition-colors"
            >
              <span className="font-mono text-[10px] tracking-[0.28em] text-[#52525b] group-hover:text-[#ff5a00] transition-colors">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="font-display text-2xl text-[#ededed] mt-2">{c.name}</h3>
              <p className="font-body text-sm text-[#a1a1aa] mt-2 leading-relaxed">{c.note}</p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
